import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Bug, ChevronDown, Copy, CheckCircle2 } from 'lucide-react';
interface Props {
  children: React.ReactNode;
  sectionName: string;
  sectionLabel?: string;
  onRetry?: () => void;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  copied: boolean;
  showDetails: boolean;
}
export class AdminSectionBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
      showDetails: false
    };
  }
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo
    });
    // Log to console for debugging
    console.error(`[AdminSection "${this.props.sectionName}"] crashed:`, error, errorInfo);
  }
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
      showDetails: false
    });
    this.props.onRetry?.();
  };
  handleCopyError = () => {
    const {
      error,
      errorInfo
    } = this.state;
    const text = [`Section: ${this.props.sectionName}`, `Error: ${error?.message}`, `Stack: ${error?.stack}`, `Component Stack: ${errorInfo?.componentStack}`].join('\n\n');
    navigator.clipboard.writeText(text);
    this.setState({
      copied: true
    });
    setTimeout(() => this.setState({
      copied: false
    }), 2000);
  };
  render() {
    if (this.state.hasError) {
      const label = this.props.sectionLabel || this.props.sectionName;
      return <div className="flex items-center justify-center min-h-[400px] p-6" data-id="element-6957">
          <div className="w-full max-w-2xl" data-id="element-6958">
            {/* Error Card */}
            <div className="bg-white rounded-2xl border-2 border-rose-200 shadow-lg overflow-hidden" data-id="element-6959">
              {/* Header */}
              <div className="bg-gradient-to-r from-rose-50 to-amber-50 px-6 py-5 border-b border-rose-100" data-id="element-6960">
                <div className="flex items-start gap-4" data-id="element-6961">
                  <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0" data-id="element-6962">
                    <Bug className="w-6 h-6 text-rose-500" data-id="element-6963" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6964">
                    <h3 className="text-base font-bold text-slate-900" data-id="element-6965">
                      Раздел «{label}» временно недоступен
                    </h3>
                    <p className="text-sm text-slate-500 mt-1" data-id="element-6966">
                      Произошла ошибка при загрузке. Остальные разделы работают
                      нормально.
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4" data-id="element-6967">
                {/* Quick tips */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-6968">
                  <div className="flex items-center gap-2 mb-2" data-id="element-6969">
                    <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-6970" />
                    <span className="text-xs font-bold text-amber-800 uppercase tracking-wider" data-id="element-6971">
                      Что можно сделать
                    </span>
                  </div>
                  <ul className="text-sm text-amber-900 space-y-1.5" data-id="element-6972">
                    <li className="flex items-start gap-2" data-id="element-6973">
                      <span className="text-amber-400 mt-0.5" data-id="element-6974">•</span>
                      Нажмите «Перезагрузить раздел» — часто помогает
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6975">
                      <span className="text-amber-400 mt-0.5" data-id="element-6976">•</span>
                      Переключитесь на другой раздел в меню слева
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6977">
                      <span className="text-amber-400 mt-0.5" data-id="element-6978">•</span>
                      Скопируйте ошибку и отправьте разработчику
                    </li>
                  </ul>
                </div>

                {/* Error details (collapsible) */}
                {this.state.error && <div data-id="element-6979">
                    <button onClick={() => this.setState({
                  showDetails: !this.state.showDetails
                })} className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-id="element-6980">
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${this.state.showDetails ? 'rotate-180' : ''}`} data-id="element-6981" />
                      Техническая информация
                    </button>
                    {this.state.showDetails && <div className="mt-2 relative" data-id="element-6982">
                        <pre className="p-3 bg-slate-900 rounded-lg text-[11px] text-slate-300 overflow-auto max-h-40 font-mono leading-relaxed" data-id="element-6983">
                          {this.state.error.message}
                          {this.state.errorInfo?.componentStack && <>
                              {'\n\nComponent Stack:'}
                              {this.state.errorInfo.componentStack}
                            </>}
                        </pre>
                        <button onClick={this.handleCopyError} className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Скопировать ошибку" data-id="element-6984">
                          {this.state.copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" data-id="element-6985" /> : <Copy className="w-3.5 h-3.5" data-id="element-6986" />}
                        </button>
                      </div>}
                  </div>}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center gap-3" data-id="element-6987">
                <button onClick={this.handleRetry} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-6988">
                  <RefreshCw className="w-4 h-4" data-id="element-6989" />
                  Перезагрузить раздел
                </button>
                <button onClick={this.handleCopyError} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-600 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-6990">
                  {this.state.copied ? <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6991" />
                      Скопировано
                    </> : <>
                      <Copy className="w-4 h-4" data-id="element-6992" />
                      Скопировать
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}